import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { User, Mail, Phone, MapPin, Edit2, Save, X } from 'lucide-react';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { userAPI, paymentAPI } from '../../services/api';
import { useAuth } from '../../hooks/useAuth';

export default function ProfilePage() {
  const { user, loading: authLoading } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [orders, setOrders] = useState([]);
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    mobile: '',
    address: ''
  });
  const navigate = useNavigate();

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/signin');
      return;
    }
    if (user) {
      setFormData({
        name: user.name || '',
        email: user.email || '',
        mobile: user.mobile || '',
        address: user.address || ''
      });
      fetchOrders();
    }
  }, [user, authLoading, navigate]);

  const fetchOrders = async () => {
    if (!user) return;
    setLoadingOrders(true);
    try {
      const userId = user.userID || user.id;
      const response = await paymentAPI.getAllOrders(userId);
      if (response.data.status === 200) {
        setOrders(response.data.data || []);
      }
    } catch (error) {
      // console.error('Error fetching orders:', error);
    } finally {
      setLoadingOrders(false);
    }
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSave = async () => {
    // Validation
    if (!formData.name.trim() || formData.name.length < 2) {
      toast.error('Name must be at least 2 characters');
      return;
    }
    if (!formData.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      toast.error('Please enter a valid email address');
      return;
    }
    if (!formData.mobile.trim() || !/^[6-9]\d{9}$/.test(formData.mobile)) {
      toast.error('Please enter a valid mobile number');
      return;
    }
    if (!formData.address.trim() || formData.address.length < 10) {
      toast.error('Address must be at least 10 characters');
      return;
    }
    if (formData.address.length > 255) {
      toast.error('Address must be less than 255 characters');
      return;
    }

    // Check what data has changed
    const changedData = { userID: user.userID.toString() };
    if (formData.name !== user.name) changedData.fullName = formData.name;
    if (formData.email !== user.email) changedData.emailID = formData.email;
    if (formData.mobile !== user.mobile) changedData.mobileNo = formData.mobile;
    if (formData.address !== user.address) changedData.address = formData.address;

    // If no data changed, don't make API call
    if (Object.keys(changedData).length === 1) {
      toast.info('No changes detected');
      setIsEditing(false);
      return;
    }

    // Confirmation dialog
    const confirmed = window.confirm('Are you sure you want to update your profile?');
    if (!confirmed) return;

    setLoading(true);
    try {
      // console.log('Sending update request:', changedData);
      const response = await userAPI.update(changedData);
      // console.log('Update response:', response);
      
      if (response.data.statusCode === 200) {
        const updatedUser = {
          ...user,
          name: formData.name,
          email: formData.email,
          mobile: formData.mobile,
          address: formData.address
        };
        
        localStorage.setItem('user', JSON.stringify(updatedUser));
        setIsEditing(false);
        window.location.reload();
        
        toast.success('Profile updated successfully!');
      } else {
        toast.error(response.data.message || 'Update failed');
      }
    } catch (error) {
      // console.error('Update error:', error);
      if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error('Update failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setFormData({
      name: user.name || '',
      email: user.email || '',
      mobile: user.mobile || '',
      address: user.address || ''
    });
    setIsEditing(false);
  };

  if (!user) return null;

  return (
    <div className="cs_profile_page">
      <div className="container">
        <div className="cs_profile_container">
          <div className="cs_profile_header">
            <div className="cs_profile_avatar">
              <User size={48} />
            </div>
            <h2>My Profile</h2>
            {!isEditing && (
              <button 
                className="cs_edit_btn"
                onClick={() => setIsEditing(true)}
              >
                <Edit2 size={16} />
                Edit Profile
              </button>
            )}
          </div>

          <div className="cs_profile_content">
            <div className="cs_profile_field">
              <div className="cs_field_icon">
                <User size={20} />
              </div>
              <div className="cs_field_content">
                <label>Full Name</label>
                {isEditing ? (
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    className="cs_profile_input"
                  />
                ) : (
                  <span>{user.name}</span>
                )}
              </div>
            </div>

            <div className="cs_profile_field">
              <div className="cs_field_icon">
                <Mail size={20} />
              </div>
              <div className="cs_field_content">
                <label>Email Address</label>
                {isEditing ? (
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className="cs_profile_input"
                  />
                ) : (
                  <span>{user.email}</span>
                )}
              </div>
            </div>

            <div className="cs_profile_field">
              <div className="cs_field_icon">
                <Phone size={20} />
              </div>
              <div className="cs_field_content">
                <label>Mobile Number</label>
                {isEditing ? (
                  <input
                    type="tel"
                    name="mobile"
                    value={formData.mobile}
                    onChange={handleInputChange}
                    className="cs_profile_input"
                  />
                ) : (
                  <span>{user.mobile}</span>
                )}
              </div>
            </div>

            <div className="cs_profile_field">
              <div className="cs_field_icon">
                <MapPin size={20} />
              </div>
              <div className="cs_field_content">
                <label>Address</label>
                {isEditing ? (
                  <textarea
                    name="address"
                    value={formData.address}
                    onChange={handleInputChange}
                    className="cs_profile_input cs_textarea"
                    rows="3"
                  />
                ) : (
                  <span>{user.address}</span>
                )}
              </div>
            </div>

            {isEditing && (
              <div className="cs_profile_actions">
                <button 
                  className="cs_save_btn" 
                  onClick={handleSave}
                  disabled={loading}
                >
                  <Save size={16} />
                  {loading ? 'Updating...' : 'Save Changes'}
                </button>
                <button className="cs_cancel_btn" onClick={handleCancel}>
                  <X size={16} />
                  Cancel
                </button>
              </div>
            )}
          </div>
        </div>

        <div className="cs_orders_container">
          <h3 className="cs_orders_title">My Orders</h3>
          {loadingOrders ? (
            <div className="cs_loading">Loading orders...</div>
          ) : orders.length > 0 ? (
            <div className="cs_orders_list">
              {orders.map((order, index) => (
                <div key={index} className="cs_order_item">
                  <div className="cs_order_header">
                    <span className="cs_order_id">Order #{order.order_id}</span>
                    <span className={`cs_order_status cs_status_${order.status}`}>
                      {order.status}
                    </span>
                  </div>
                  <div className="cs_order_details">
                    <p><strong>Amount:</strong> ₹{order.amount}</p>
                    <p><strong>Date:</strong> {new Date(order.created_at).toLocaleDateString()}</p>
                    {order.phonepe_payment_id && (
                      <p><strong>UPI Transaction ID:</strong> {order.phonepe_payment_id}</p>
                    )}
                    {order.payment_method && (
                      <p><strong>Payment Method:</strong> {order.payment_method}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="cs_no_orders">
              <p>No orders found</p>
            </div>
          )}
        </div>
      </div>
      
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />

      <style jsx>{`
        .cs_profile_page {
          min-height: 100vh;
          background: #f8f8f8;
          padding: 120px 0 80px;
        }
        .cs_profile_container {
          max-width: 700px;
          margin: 0 auto;
          background: #ffffff;
          border-radius: 15px;
          box-shadow: 0 8px 25px rgba(18, 18, 18, 0.08);
          border: 1px solid #dddddd;
          overflow: hidden;
        }
        .cs_profile_header {
          background: #121212;
          color: #ffffff;
          padding: 50px 40px;
          text-align: center;
          position: relative;
        }
        .cs_profile_avatar {
          width: 100px;
          height: 100px;
          background: rgba(0, 218, 141, 0.15);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 25px;
          border: 3px solid #0b5e4b;
        }
        .cs_profile_header h2 {
          margin: 0 0 25px;
          font-size: 32px;
          font-weight: 700;
          color: #ffffff;
        }
        .cs_edit_btn {
          background: transparent;
          border: 2px solid #0b5e4b;
          color: #0b5e4b;
          padding: 12px 25px;
          border-radius: 15px;
          cursor: pointer;
          display: inline-flex;
          align-items: center;
          gap: 10px;
          font-size: 14px;
          font-weight: 500;
          transition: all 0.3s ease;
        }
        .cs_edit_btn:hover {
          background: #0b5e4b;
          color: #ffffff;
          transform: translateY(-2px);
        }
        .cs_profile_content {
          padding: 50px 40px;
          background: #ffffff;
        }
        .cs_profile_field {
          display: flex;
          align-items: flex-start;
          gap: 20px;
          margin-bottom: 35px;
          padding: 25px;
          background: #f8f8f8;
          border-radius: 15px;
          // border-left: 4px solid #0b5e4b;
        }
        .cs_field_icon {
          width: 50px;
          height: 50px;
          background: #121212;
          border-radius: 15px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #0b5e4b;
          flex-shrink: 0;
        }
        .cs_field_content {
          flex: 1;
        }
        .cs_field_content label {
          display: block;
          font-size: 14px;
          color: #4f4747;
          margin-bottom: 8px;
          font-weight: 500;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }
        .cs_field_content span {
          font-size: 18px;
          color: #121212;
          font-weight: 500;
          line-height: 1.4;
        }
        .cs_profile_input {
          width: 100%;
          padding: 15px 20px;
          border: 2px solid #dddddd;
          border-radius: 15px;
          font-size: 16px;
          transition: all 0.3s ease;
          background: #ffffff;
          color: #121212;
        }
        .cs_profile_input:focus {
          outline: none;
          border-color: #0b5e4b;
          box-shadow: 0 0 0 3px rgba(0, 218, 141, 0.1);
        }
        .cs_textarea {
          resize: vertical;
          min-height: 100px;
          font-family: inherit;
        }
        .cs_profile_actions {
          display: flex;
          gap: 20px;
          justify-content: center;
          margin-top: 50px;
        }
        .cs_save_btn, .cs_cancel_btn {
          padding: 15px 30px;
          border-radius: 15px;
          cursor: pointer;
          display: inline-flex;
          align-items: center;
          gap: 10px;
          font-size: 16px;
          font-weight: 500;
          transition: all 0.3s ease;
          min-width: 140px;
          justify-content: center;
        }
        .cs_save_btn {
          background: #0b5e4b;
          color: #ffffff;
          border: 2px solid #0b5e4b;
        }
        .cs_save_btn:hover {
          background: transparent;
          color: #0b5e4b;
          transform: translateY(-2px);
        }
        .cs_cancel_btn {
          background: transparent;
          color: #4f4747;
          border: 2px solid #dddddd;
        }
        .cs_cancel_btn:hover {
          border-color: #121212;
          color: #121212;
          transform: translateY(-2px);
        }
        .cs_orders_container {
          max-width: 700px;
          margin: 30px auto 0;
          background: #ffffff;
          border-radius: 15px;
          box-shadow: 0 8px 25px rgba(18, 18, 18, 0.08);
          border: 1px solid #dddddd;
          padding: 30px;
        }
        .cs_orders_title {
          font-size: 24px;
          font-weight: 600;
          margin-bottom: 20px;
          color: #121212;
        }
        .cs_order_item {
          border: 1px solid #e0e0e0;
          border-radius: 10px;
          padding: 20px;
          background: #f9f9f9;
          margin-bottom: 15px;
        }
        .cs_order_header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 15px;
        }
        .cs_order_id {
          font-weight: 600;
          color: #121212;
        }
        .cs_order_status {
          padding: 5px 12px;
          border-radius: 20px;
          font-size: 12px;
          font-weight: 600;
          text-transform: uppercase;
        }
        .cs_status_paid {
          background: #d4edda;
          color: #155724;
        }
        .cs_status_delivered {
          background: #d1ecf1;
          color: #0c5460;
        }
        .cs_status_created {
          background: #fff3cd;
          color: #856404;
        }
        .cs_status_cancelled {
          background: #f8d7da;
          color: #721c24;
        }
        .cs_order_details p {
          margin: 5px 0;
          font-size: 14px;
          color: #666;
        }
        .cs_no_orders {
          text-align: center;
          padding: 40px;
          color: #666;
        }
        .cs_loading {
          text-align: center;
          padding: 20px;
          color: #666;
        }
        @media (max-width: 768px) {
          .cs_profile_page {
            padding: 100px 0 50px;
          }
          .cs_profile_container {
            margin: 0 20px;
            border-radius: 15px;
          }
          .cs_profile_header {
            padding: 40px 25px;
          }
          .cs_profile_content {
            padding: 40px 25px;
          }
          .cs_profile_field {
            padding: 20px;
            gap: 15px;
          }
          .cs_field_icon {
            width: 45px;
            height: 45px;
          }
          .cs_profile_actions {
            flex-direction: column;
            gap: 15px;
          }
          .cs_orders_container {
            margin: 20px;
            padding: 20px;
          }
        }
      `}</style>
    </div>
  );
}